import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import AddGuestDialog from '../components/AddGuestDialog';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Clock,
  Users,
  QrCode,
  Edit,
  Share2,
  Download,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const EventDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [eventGuests, setEventGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const loadEvent = () => {
    setLoading(true);
    api
      .get(`/events/${id}`)
      .then((res) => {
        setEvent(res.data);
        setEventGuests(res.data.guests || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Event not found</h2>
        <Button onClick={() => navigate('/dashboard/events')}>Back to Events</Button>
      </div>
    );
  }

  const attendedCount = eventGuests.filter((g) => g.attended).length;
  const attendanceRate = eventGuests.length > 0
    ? ((attendedCount / eventGuests.length) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/events')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{event.name}</h1>
          <p className="text-gray-600 mt-1">Event Details & Management</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/dashboard/events/${id}/edit`)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => navigate(`/scan/${id}`)}>
            <QrCode className="w-4 h-4 mr-2" />
            Scan QR
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Guests</p>
              <p className="text-2xl font-bold">{event.totalGuests}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Registered</p>
              <p className="text-2xl font-bold">{event.registeredGuests}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Attended</p>
              <p className="text-2xl font-bold">{attendedCount}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Rate</p>
              <p className="text-2xl font-bold">{attendanceRate}%</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-6">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="guests">Guests ({eventGuests.length})</TabsTrigger>
          <TabsTrigger value="sessions">Sessions ({event.sessions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Event Information</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="font-semibold">
                    {new Date(event.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Time</p>
                  <p className="font-semibold">{event.time}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Venue</p>
                  <p className="font-semibold">{event.venue}</p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">Description</p>
                <p className="text-gray-800">{event.description}</p>
              </div>
              <div className="pt-4 border-t">
                <Badge
                  className={`${
                    event.status === 'upcoming'
                      ? 'bg-green-100 text-green-700 hover:bg-green-100'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                </Badge>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Button
                variant="outline"
                className="justify-start h-auto py-4"
                onClick={() => navigate(`/scan/${id}`)}
              >
                <QrCode className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Scan QR Codes</div>
                  <div className="text-xs text-gray-500">Mark attendance</div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="justify-start h-auto py-4"
                onClick={() => window.open(`/scan/${id}`, '_blank')}
              >
                <Share2 className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Share Scanner Link</div>
                  <div className="text-xs text-gray-500">Public QR scanner</div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="justify-start h-auto py-4"
                onClick={() => navigate(`/dashboard/events/${id}/qr`)}
              >
                <Download className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Download QR Codes</div>
                  <div className="text-xs text-gray-500">Guest QR codes</div>
                </div>
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="guests">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Guest List</h2>
              <AddGuestDialog
                eventId={id}
                onGuestAdded={loadEvent}
                trigger={
                  <Button variant="outline" size="sm">
                    Add Guest
                  </Button>
                }
              />
            </div>
            <div className="space-y-2">
              {eventGuests.map((guest) => (
                <div
                  key={guest.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition"
                >
                  <div className="flex-1">
                    <p className="font-semibold">{guest.name}</p>
                    <p className="text-sm text-gray-600">{guest.email}</p>
                    <p className="text-sm text-gray-500">{guest.company}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {guest.attended ? (
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Attended
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">
                        <XCircle className="w-3 h-3 mr-1" />
                        Not Attended
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
              {eventGuests.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No guests added yet</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sessions">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Event Sessions</h2>
            <div className="space-y-3">
              {event.sessions.map((session) => (
                <div key={session.id} className="p-4 border rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{session.name}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {session.time}
                      </p>
                    </div>
                    <Badge variant="outline">Max {session.maxEntry} entry</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EventDetailsPage;